import { IconButton } from '@material-ui/core';
import { ChevronLeft, ChevronRight, Close, SaveAlt } from '@material-ui/icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';

import { usePresenter } from '../../contexts/PresenterContext';
import useCollectPresentation from './useCollectPresentation';
import useReportPresenterHeight from './useReportPresenterHeight';
import { PresenterHelper } from './presenter-helper';
import { usePlayer } from '../../contexts/PlayerContext';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import no_thumbnail from '../../assets/icons/no-thumbnail.svg';
import { getEmbedShareSettings, joinClassNames } from '../../util/util';
import { OpenInFull } from '../QPlayerSVGElement';
import { FetchDataHelper } from '../Player/hooks/fetch-data-helper';

import './Presenter.scss';

interface PresenterProps {
    settings?: any;
    children?: React.ReactNode;
}

// How long a backwards jump in the live position is held before it is believed - see the
// `liveSlidePosition` effect. Long enough to ride out reporting jitter, short enough that a real
// seek is followed almost immediately.
const LIVE_POSITION_DIP_GRACE_MS = 1500;

// Mirrors GoBrain's `_minDvrWindow` (media-player/video-js-8.js): a live stream shorter than
// this is reported as 'Live' rather than 'Dvr' and cannot be seeked.
const LIVE_MIN_DVR_WINDOW = 120;

const baseClass = 'qplayer-presenter';
// Deliberately not a `qplayer-presenter--*` modifier: it must not match the `.qplayer-presenter`
// and `.qplayer-presenter__player` selectors used by the stylesheet and by the fullscreen /
// chat / transcription lookups, which all mean "the slides layout is active".
const passthroughClass = 'qplayer-presenter-passthrough';

const Presenter = ({ settings, children }: PresenterProps) => {
    const { t: translator } = useTranslation();
    const { slideTracks, slideCount } = useCollectPresentation({});
    const { presentationPublishedSlides } = usePresenter();
    const { currentPosition, duration, isPlaying, showChatWindow, setPlayerCurrentPosition, setShowChatWindow, seenEngagementCounts, setSeenEngagementCounts, setStartPlayerAt } = usePlayer();
    const { playlistId, currentMediaId, isMediaLive, mediasData, configParams, viewerEngagementModuleActivated, mediaName,  embedShareSettings: embedShareSettingsMessage, showCountdownModule, countdownStartTime, showRegistrationForm } = useConfiguration();

    const primaryColor = settings?.playerStyling?.primaryColor ?? '#126cfc';
    // The preset's font, named the same way the runtime player-styling sheet names it (see
    // `sheetPlayerFontPlayerStyling` in util/util.js). 'Default' means "whatever the player
    // inherits" - it is a reserved keyword in font-family, so the declaration is dropped and
    // inheritance takes over, which is exactly what that option asks for.
    const playerFont = settings?.playerStyling?.useCustomFont
        ? settings?.playerStyling?.playerCustomFontName
        : settings?.playerStyling?.playerFont;
    // A di-loader embed (`data-qplayer-bonnier-di`) brings DI's own faces, which take over from
    // the preset font - the countdown title and the register form do the same. The heading face
    // is the condensed black; the button is a label, so it takes the text bold.
    const headingFont = configParams.isBonnierDi ? "'BentonSansCondDI Black', sans-serif" : playerFont;
    const labelFont = configParams.isBonnierDi ? "'BentonSansDI Bold', sans-serif" : undefined;
    // Falls back to the colour the stylesheet gives the slide labels, so a preset without a
    // secondary colour looks exactly as it did before.
    const secondaryColor = tinycolor({
        ...tinycolor(settings?.playerStyling?.secondaryColor ?? '#6B7280').toRgb(),
        a: Number(settings?.playerStyling?.secondaryOpacity ?? 100) / 100,
    }).toRgbString();
    const playerBGColorWithOpacity = tinycolor({
        ...tinycolor(settings?.playerStyling?.playerBackgroundColor ?? '#000000').toRgb(),
        a: Number(settings?.playerStyling?.playerBackgroundOpacity ?? 100) / 100,
    }).toRgbString();
    const currentMedia = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);
    // The live position this component trusts, plus the furthest point the stream has played.
    //
    // Slides are REVEALED against `liveRevealedUpTo` (a high-water mark) so a slide never appears
    // before the stream reaches it and seeking back inside the DVR window does not take slides
    // away again. The ACTIVE slide is chosen with `liveSlidePosition`, which is the live position
    // with backwards jumps held back: the reported live position is not strictly monotonic, and an
    // unfiltered dip across the boundary of a just-published slide flipped the active slide to the
    // new one, back to the previous one and forward again. The main cause of those dips is fixed in
    // Player.tsx (see `latestLiveDuration`); this guard keeps the visible slide steady through any
    // that remain, from rounding at the live edge to a stall.
    //
    // A backwards move is only believed once it has persisted for LIVE_POSITION_DIP_GRACE_MS -
    // jitter recovers well inside that, a real seek keeps reporting the lower position and is taken
    // on the next event after the grace. A seek this component starts itself is taken immediately.
    const [liveSlidePosition, setLiveSlidePosition] = useState(0);
    const [liveRevealedUpTo, setLiveRevealedUpTo] = useState(0);
    const acceptedLivePositionRef = useRef(0);
    const dipSinceRef = useRef<number | null>(null);

    const acceptLivePosition = useCallback((position: number) => {
        acceptedLivePositionRef.current = position;
        dipSinceRef.current = null;
        setLiveSlidePosition(position);
        setLiveRevealedUpTo((previous) => (position > previous ? position : previous));
    }, []);

    useEffect(() => {
        acceptedLivePositionRef.current = 0;
        dipSinceRef.current = null;
        setLiveSlidePosition(0);
        setLiveRevealedUpTo(0);
    }, [currentMediaId]);

    useEffect(() => {
        if (!isMediaLive || typeof currentPosition !== 'number') {
            return;
        }
        if (currentPosition >= acceptedLivePositionRef.current) {
            acceptLivePosition(currentPosition);
            return;
        }
        if (dipSinceRef.current === null) {
            dipSinceRef.current = Date.now();
            return;
        }
        if (Date.now() - dipSinceRef.current < LIVE_POSITION_DIP_GRACE_MS) {
            return;
        }
        acceptLivePosition(currentPosition);
    }, [isMediaLive, currentPosition, acceptLivePosition]);

    const slideImageSource = useMemo(() => FetchDataHelper.convertToSlideSource(currentMedia?.images?.filter((img: any) => img.collectionId?.startsWith("slide-") || img.id?.startsWith("slide-")) || []), [currentMedia]);
    const { currentSlide: currentLiveSlide, currentSlideIndex: currentLiveSlideIndex, presentedSlides: currentLivePresentedSlides } = PresenterHelper.getLiveCurrentSlide(liveSlidePosition, presentationPublishedSlides, slideImageSource, liveRevealedUpTo) as any;

    const currentSlides = slideTracks.filter(Boolean);
    const { currentSlide: currentRecordedSlide, currentSlideIndex: currentRecordedSlideIndex } = PresenterHelper.getCurrentSlide(currentPosition, slideTracks) as any;
    const currentSlide = isMediaLive ? isPlaying !== undefined ? currentLiveSlide : null : currentRecordedSlide;
    const currentSlideIndex = isMediaLive ? currentLiveSlideIndex : currentRecordedSlideIndex;
    const currentPresentedSlides = isMediaLive ? currentLivePresentedSlides : currentSlides;
    const currentPlayerId = useMemo(() => `player-${playlistId || currentMediaId}-${configParams.containerKey}`, [currentMediaId, playlistId, configParams.containerKey]);

    // For live, everything the UI counts and navigates must come from the slides the viewer has
    // reached (see PresenterHelper.getLiveCurrentSlide) - not from the media's pdf-track, which
    // is fetched once and both lags behind and contains slides the stream hasn't played yet.
    const totalSlideCount = isMediaLive ? currentPresentedSlides.length : slideCount;
    const navigableSlides = isMediaLive ? currentPresentedSlides : slideTracks;

    // A live stream can only be seeked inside its DVR window, and GoBrain does not treat a live
    // stream as seekable at all until that window is at least LIVE_MIN_DVR_WINDOW long (its own
    // `_minDvrWindow`). `duration` carries the window length for live, so below that threshold
    // clicking a slide cannot go anywhere and the controls are disabled instead of doing nothing.
    const canJumpToSlide = !isMediaLive || (typeof duration === 'number' && duration >= LIVE_MIN_DVR_WINDOW);

    const slidesTrackRef = useRef<HTMLDivElement>(null);
    const activeSlideThumbnailRef = useRef<HTMLDivElement>(null);
    const presenterRootRef = useRef<HTMLDivElement>(null);
    const slideFullscreenDialogRef = useRef<HTMLDialogElement>(null);
    const presenterContentRef = useRef<HTMLDivElement>(null);
    const [canScrollSlides, setCanScrollSlides] = useState(false);
    const [canScrollSlidesLeft, setCanScrollSlidesLeft] = useState(false);
    const [canScrollSlidesRight, setCanScrollSlidesRight] = useState(false);
    const [openCurrentSlideFullscreen, setOpenCurrentSlideFullscreen] = useState(false);

    const goToNextSlide = () => {
        if (currentSlideIndex < navigableSlides.length - 1) {
            const nextSlide = navigableSlides[currentSlideIndex + 1];
            nextSlide && jumpToSlide(nextSlide);
        }
    };

    const goToPreviousSlide = () => {
        if (currentSlideIndex > 0) {
            const prevSlide = navigableSlides[currentSlideIndex - 1];
            prevSlide && jumpToSlide(prevSlide);
        }
    };

    const jumpToSlide = (slide: any) => {
        if (typeof slide.start !== "number" || !canJumpToSlide) {
            return;
        }

        if (isMediaLive) {
            // GoBrain seeks a live stream by an offset from the live edge: `position()` turns a
            // NEGATIVE value into `seekable().end(0) + value`. Slide starts are on the same axis
            // as `currentPosition` (seconds from the start of the DVR window) and `duration` is
            // the window length, so `start - duration` is exactly that offset. A value at or
            // above 0 means the slide sits at/after the live edge, i.e. the stream has not
            // produced it yet - nothing to seek to.
            const offsetFromLiveEdge = slide.start - duration;
            if (offsetFromLiveEdge >= 0) {
                return;
            }
            (window as any).GoBrain.widgets(currentPlayerId)?.position?.(offsetFromLiveEdge);
            acceptLivePosition(slide.start);
            setPlayerCurrentPosition(slide.start);
            return;
        }

        const slideIndex = slideTracks.findIndex(s => s.id === slide.id);
        if (slideIndex !== -1) {
            if (isPlaying === undefined) {
                (window as any).GoBrain.widgets(currentPlayerId)?.play?.();
                setPlayerCurrentPosition(slide.start);
                setStartPlayerAt(slide.start);
            } else {
                (window as any).GoBrain.widgets(currentPlayerId)?.position?.(slide.start);
                setPlayerCurrentPosition(slide.start);
            }
        }
    }

    useEffect(() => {
        const slidesTrackEl = slidesTrackRef.current;
        if (!slidesTrackEl) return;
        const checkScroll = () => {
            setCanScrollSlides(slidesTrackEl.scrollWidth > slidesTrackEl.clientWidth);
            setCanScrollSlidesLeft(slidesTrackEl.scrollLeft > 0);
            setCanScrollSlidesRight(slidesTrackEl.scrollWidth - slidesTrackEl.scrollLeft - slidesTrackEl.clientWidth > 1);
        };
        checkScroll();
        slidesTrackEl.addEventListener('scroll', checkScroll);
        const resizeObserver = new ResizeObserver(checkScroll);
        resizeObserver.observe(slidesTrackEl);
        return () => {
            slidesTrackEl.removeEventListener('scroll', checkScroll);
            resizeObserver.disconnect();
        };
    }, [currentSlides.length]);

    // `scrollIntoView` walks every scrollable ancestor, so a slide change dragged the panel and
    // the host page along with it. Only the carousel moves now, and only far enough to bring a
    // thumbnail that has gone out of sight back to the edge - one already in view is left alone.
    useEffect(() => {
        const slidesTrackEl = slidesTrackRef.current;
        const thumbnailEl = activeSlideThumbnailRef.current;
        if (!slidesTrackEl || !thumbnailEl) {
            return;
        }

        const trackBounds = slidesTrackEl.getBoundingClientRect();
        const thumbnailBounds = thumbnailEl.getBoundingClientRect();
        const hiddenToTheLeft = thumbnailBounds.left - trackBounds.left;
        const hiddenToTheRight = thumbnailBounds.right - trackBounds.right;

        if (hiddenToTheLeft >= 0 && hiddenToTheRight <= 0) {
            return;
        }

        slidesTrackEl.scrollBy({
            left: hiddenToTheLeft < 0 ? hiddenToTheLeft : hiddenToTheRight,
            behavior: 'smooth',
        });
    }, [currentSlide?.id]);

    // Shown as a modal <dialog>, which the browser puts in its top layer - the only way to sit
    // above host page content, since an embedded player is usually inside a stacking context it
    // cannot escape with z-index. Deliberately NOT requestFullscreen(): that would flip the
    // player's own fullscreen state, which keys off `document.fullscreenElement` alone.
    const toggleSlideFullscreen = () => {
        const dialog = slideFullscreenDialogRef.current;
        if (!dialog) {
            return;
        }
        if (dialog.open) {
            dialog.close();
            return;
        }
        dialog.showModal();
        setOpenCurrentSlideFullscreen(true);
    };

    // A modal <dialog> sits above everything but does not stop what is behind it from scrolling,
    // and the panel is itself a scroller. Locked while the slide is open, put back exactly as it
    // was on close.
    useEffect(() => {
        if (!openCurrentSlideFullscreen) {
            return;
        }
        const root = presenterRootRef.current;
        const previousRootOverflow = root?.style.overflow ?? '';
        const previousBodyOverflow = document.body.style.overflow;

        if (root) {
            root.style.overflow = 'hidden';
        }
        document.body.style.overflow = 'hidden';

        return () => {
            if (root) {
                root.style.overflow = previousRootOverflow;
            }
            document.body.style.overflow = previousBodyOverflow;
        };
    }, [openCurrentSlideFullscreen]);

    const scrollSlides = (direction: 'prev' | 'next') => {
        const slidesTrackEl = slidesTrackRef.current;
        if (!slidesTrackEl) return;
        const amount = slidesTrackEl.clientWidth;
        slidesTrackEl.scrollBy({ left: direction === 'prev' ? -amount : amount, behavior: 'smooth' });
    };

    const downloadFile = async (url: string, name?: string) => {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = name ?? '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    // Same condition CountDown renders under: the countdown covers the whole embed, so the slides
    // layout falls back to the pass-through wrappers until it is gone.
    const isCountdownShown = (!!showCountdownModule || !!countdownStartTime) && !showRegistrationForm;
    const hasPresentation = !isCountdownShown && PresenterHelper.hasPresentationLayout({ publishedSlides: presentationPublishedSlides, media: currentMedia });

    // `hasPresentation` cannot be known on the first render: it depends on media data that only
    // arrives once the player is running. Returning a different element tree before and after
    // that point makes React discard and rebuild this subtree, which destroys the container
    // GoBrain binds to and leaves a half-initialised widget behind. So the wrapper elements are
    // always rendered in the same shape and only their class names change: without a
    // presentation they carry pass-through names that no `.qplayer-presenter*` rule or
    // `querySelector` matches, and that collapse to `display: contents` so the player lays out
    // exactly as if this component were not here.
    const layoutClass = hasPresentation ? baseClass : passthroughClass;

    // Nothing is being presented before a live goes on air (and briefly whenever a presentation is
    // stopped), which left the slide panel empty next to the pre-cover. Fall back to the deck's
    // first page so the panel always shows something. It is a still preview, not the current
    // slide: the strip highlights nothing and the counter is unchanged, so it cannot be mistaken
    // for "this is what is on screen now".
    const firstSlideImageUrl = useMemo(() => {
        const firstSequenceWithImage = slideTracks.find((slide: any) => slide?.images?.[0]?.url);
        return firstSequenceWithImage?.images?.[0]?.url ?? slideImageSource?.[0]?.url;
    }, [slideTracks, slideImageSource]);

    const activeSlideImageUrl = currentSlide?.images?.[0]?.url ?? firstSlideImageUrl;
    const isShowingSlidePreview = !currentSlide?.images?.[0]?.url && !!firstSlideImageUrl;

    const embedShareSettings = getEmbedShareSettings({ configParams, messageData: embedShareSettingsMessage });
	const isInfoExcludedOnThisHost = (option: any, domainList: any) => {
		let isInfoHidden = false;

		const titleDomainList = (domainList ?? '').split(',');

		if (option && titleDomainList.length !== 0) {
			const url = embedShareSettings?.origin ?? '';
			isInfoHidden = titleDomainList.some((domain: any) => domain && url.includes(domain.trim()));
		}

		return isInfoHidden;
	};

	const enabledTopControlByPreset = settings.topControls?.enabled;
	const mediaTitleHiddenOnThisHost = isInfoExcludedOnThisHost(
		settings.topControls?.showExcludingTitleOnDomains,
		settings.topControls?.excludingTitleOnDomains
	);

    useReportPresenterHeight({
        rootRef: presenterRootRef,
        contentRef: presenterContentRef,
        hasPresentation,
        mediaId: currentMediaId,
        qplayerId: configParams.qplayerId,
    });

    return (
        <div className={layoutClass} ref={presenterRootRef}>
            {hasPresentation && enabledTopControlByPreset && settings.topControls?.showTitle && !mediaTitleHiddenOnThisHost && <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header-title`} style={{ fontFamily: headingFont || undefined }}>
                    {mediaName || currentMedia?.mediaName}
                </div>
            </div>}
            <div className={`${layoutClass}__content`} ref={presenterContentRef}>
                <div className={`${layoutClass}__player`}>
                    {children}
                </div>
                {hasPresentation && <div className={`${baseClass}__active-slide-container`}>
                    <button
                        disabled={!activeSlideImageUrl}
                        className={`${baseClass}__open-fullscreen-button`}
                        onClick={toggleSlideFullscreen}
                        title={translator("PRESENTER_MODULE_OPEN_FULLSCREEN_BUTTON")}
                    >
                        <OpenInFull/>
                    </button>
                    {activeSlideImageUrl ? <img
                        src={activeSlideImageUrl}
                        alt={isShowingSlidePreview
                            ? `${translator("PRESENTER_MODULE_SLIDE_LABEL")} 1`
                            : `${translator("PRESENTER_MODULE_SLIDE_LABEL")} ${currentSlideIndex}${translator("PRESENTER_MODULE_OUT_OF_LABEL")}${totalSlideCount}`}
                        className={`${baseClass}__slide-image--active`}
                    /> : <span>{translator("PRESENTER_MODULE_NO_SLIDE_PRESENTING")}</span>}
                </div>}
            </div>
            {hasPresentation && <div className={`${baseClass}__slide-actions`}>
                <span style={{ color: secondaryColor }}>{translator("PRESENTER_MODULE_SLIDES_LABEL")}</span>
                <IconButton
                    onClick={goToPreviousSlide}
                    title={translator("PRESENTER_MODULE_PREV_SLIDE_BUTTON")}
                    disabled={!canJumpToSlide || currentSlideIndex <= 0}
                >
                    <ChevronLeft fontSize='small' htmlColor={!canJumpToSlide || currentSlideIndex <= 0 ? '#1010104d' : undefined} />
                </IconButton>
                    <span>{`${currentSlideIndex + 1}/${totalSlideCount}`}</span>
                <IconButton
                    onClick={goToNextSlide}
                    title={translator("PRESENTER_MODULE_NEXT_SLIDE_BUTTON")}
                    disabled={!canJumpToSlide || currentSlideIndex >= totalSlideCount - 1}
                >
                    <ChevronLeft fontSize='small' htmlColor={!canJumpToSlide || currentSlideIndex >= totalSlideCount - 1 ? '#1010104d' : undefined} style={{ transform: 'rotate(180deg)' }} />
                </IconButton>
            </div>}

            {hasPresentation && <div className={`${baseClass}__slides-container`}>
                <span className={`${baseClass}__slides-title`} style={{ color: secondaryColor }}>{translator("PRESENTER_MODULE_ALL_SLIDES_LABEL")}</span>
                {(currentPresentedSlides.length === 0)
                    ? <div className={`${baseClass}__slides--empty`}>{translator("PRESENTER_MODULE_NO_SLIDES_AVAILABLE")}</div>
                    : <div className={`${baseClass}__slides`}>
                        {canScrollSlides && <button
                            className={joinClassNames(
                                `${baseClass}__prev-button`
                            )}
                            onClick={() => scrollSlides('prev')}
                            title={translator("PRESENTER_MODULE_PREV_SLIDE_BUTTON")}
                            tabIndex={canScrollSlidesLeft ? 0 : -1}
                            aria-hidden={!canScrollSlidesLeft}
                            disabled={!canScrollSlidesLeft}
                        >
                            <ChevronLeft fontSize='small' />
                        </button>}
                        <div className={`${baseClass}__slides-track`} ref={slidesTrackRef}>
                            {currentPresentedSlides.map((slide: any, index: number) => (
                                <div
                                    key={index}
                                    ref={currentSlide?.id === slide.id ? activeSlideThumbnailRef : null}
                                    className={joinClassNames(
                                        `${baseClass}__slide-thumbnail`,
                                        currentSlide?.id === slide.id ? `${baseClass}__slide-thumbnail--selected` : '',
                                        !slide.images?.[0]?.url ? `${baseClass}__slide-thumbnail--no-image` : '',
                                        !canJumpToSlide ? `${baseClass}__slide-thumbnail--not-clickable` : ''
                                    )}
                                    style={{ '--qplayer-presenter-slide-thumbnail-color': primaryColor } as React.CSSProperties}
                                    tabIndex={canJumpToSlide ? 0 : -1}
                                    onClick={() => jumpToSlide(slide)}
                                    onKeyDown={(event: React.KeyboardEvent) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            jumpToSlide(slide);
                                        }
                                    }}
                                    role="button"
                                    aria-disabled={!canJumpToSlide}
                                    aria-label={currentSlide?.id === slide.id ? `${translator("PRESENTER_MODULE_CURRENT_SLIDE_LABEL")} ${currentSlideIndex + 1}` : `${translator("PRESENTER_MODULE_GO_TO_SLIDE_LABEL")} ${index + 1}`}
                                    aria-current={currentSlide?.id === slide.id}
                                >
                                    <img
                                        aria-hidden={true}
                                        src={slide.images?.[0]?.url ?? no_thumbnail}
                                        alt={currentSlide?.id === slide.id ? `${translator("PRESENTER_MODULE_CURRENT_SLIDE_LABEL")} ${currentSlideIndex + 1}` : `${translator("PRESENTER_MODULE_GO_TO_SLIDE_LABEL")} ${index + 1}`}
                                        className={`${baseClass}__slide-image`}
                                    />
                                </div>
                            ))}
                        </div>
                        {canScrollSlides && <button
                            className={joinClassNames(
                                `${baseClass}__next-button`,
                            )}
                            onClick={() => scrollSlides('next')}
                            title={translator("PRESENTER_MODULE_NEXT_SLIDE_BUTTON")}
                            tabIndex={canScrollSlidesRight ? 0 : -1}
                            aria-hidden={!canScrollSlidesRight}
                            disabled={!canScrollSlidesRight}
                        >
                            <ChevronLeft fontSize='small' style={{ transform: 'rotate(180deg)' }} />
                        </button>}
                    </div>}

                <div className={`${baseClass}__actions`}>
                    <div className={`${baseClass}__actions-list`}>
                        {configParams.qplayerFiles?.map((file: any, index: number) => (
                        <div
                            className={`${baseClass}__action`}
                            key={index}
                            onClick={() => downloadFile(file.url, file.name)}
                            title={`${translator("DOWNLOAD_FILE")} ${file.name}`}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event: React.KeyboardEvent) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    downloadFile(file.url, file.name);
                                }
                        }}>
                            <span className={`${baseClass}__action-icon`} aria-hidden="true">{file.name.split('.').pop()?.toUpperCase()}</span>
                            <div className={`${baseClass}__action-info`} aria-hidden="true">
                                <span className={`${baseClass}__action-name`}>{file.name}</span>
                                <span className={`${baseClass}__action-size`}>{file.size ?? translator("PRESENTER_MODULE_UNKNOWN_FILE_SIZE")}</span>
                            </div>
                            <div className={`${baseClass}__action-button`} aria-hidden="true"><SaveAlt fontSize='small' /></div>
                        </div>))}
                    </div>
                    <div className={`${baseClass}__actions-list`}>
                       {viewerEngagementModuleActivated && settings.engagementFeatures.enabled && settings.engagementFeatures.showChat && <button
                            className={`${baseClass}__module-action-button`}
                            style={{ fontFamily: labelFont }}
                            onClick={() => {
                                setSeenEngagementCounts(null);
                                setShowChatWindow(true);
                            }}
                        >
                            {translator("CHAT_WINDOW_OPEN")}
                            <ChevronRight aria-hidden="true" />
                            {!showChatWindow && seenEngagementCounts && <span className={`${baseClass}__module-action-button-badge`} style={{ background: primaryColor }}></span>}
                        </button>}
                    </div>
                </div>
            </div>}
            {hasPresentation && <dialog
                ref={slideFullscreenDialogRef}
                className={`${baseClass}__slide-dialog`}
                onClose={() => setOpenCurrentSlideFullscreen(false)}
            >
                <div className={`${baseClass}__active-slide-container`}>
                    <button
                        className={`${baseClass}__open-fullscreen-button`}
                        onClick={toggleSlideFullscreen}
                        title={translator("PRESENTER_MODULE_OPEN_FULLSCREEN_BUTTON")}
                    >
                        <Close/>
                    </button>
                    {activeSlideImageUrl && <img
                        src={activeSlideImageUrl}
                        alt={`${translator("PRESENTER_MODULE_SLIDE_LABEL")} ${currentSlideIndex}`}
                        className={`${baseClass}__slide-image--active`}
                    />}
                </div>
            </dialog>}
            {hasPresentation && !currentMedia && <div aria-hidden="true" className={`${baseClass}__no-media`} style={{ backgroundColor: playerBGColorWithOpacity }}></div>}
        </div>
    );
};

export default Presenter;